import React, { useState, useEffect, useCallback } from 'react';
import { Box, Heading, Text, SimpleGrid, Checkbox, useToast, Flex, Switch, HStack } from '@chakra-ui/react';
import apiClient from '../lib/api/apiClient';
import { useUserStore } from '../store/userStore';
import { fetchUserSettings } from '../hooks/useUser';
import { formatPhoneNumber } from '../utils/stringUtils';

interface SettingsState {
  twoFactorAuth: boolean;
  smsConsent: boolean;
  twoFactorType: 'email' | 'sms';
}

const SettingsTab: React.FC = () => {
  const { user } = useUserStore();
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [settings, setSettings] = useState<SettingsState>({
    twoFactorAuth: false,
    smsConsent: false,
    twoFactorType: 'sms',
  });
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const toast = useToast();

  // Load initial settings
  useEffect(() => {
    const loadUserSettings = async () => {
      if (user?.id) {
        setIsLoadingSettings(true);
        try {
          const settings = await fetchUserSettings(user.id);
          let twoFactorType = settings.two_fa_type || 'sms';
          
          // Edge case: If 2FA is set to SMS but user has no phone or no SMS consent, switch to Email
          const hasPhone = user?.phone && user.phone.trim().length > 0;
          const hasSmsConsent = Boolean(settings.sms_consent || false);
          
          if (twoFactorType === 'sms' && (!hasPhone || !hasSmsConsent)) {
            console.log('Edge case detected: 2FA set to SMS but missing phone or consent. Switching to Email.');
            twoFactorType = 'email';
            
            // Automatically correct this in the database
            try {
              await apiClient.post('/user/update-settings', {
                user_settings: {
                  two_fa_type: 'email'
                }
              });
              console.log('Successfully corrected 2FA type to Email in database');
            } catch (updateError) {
              console.error('Failed to correct 2FA type in database:', updateError);
            }
          }
          
          setSettings({
            twoFactorAuth: Boolean(settings.two_fa_enabled || false),
            smsConsent: hasSmsConsent,
            twoFactorType: twoFactorType,
          });
        } catch (error) {
          console.error('Error loading user settings:', error);
          // Use default values if API call fails
        } finally {
          setIsLoadingSettings(false);
        }
      }
    };

    loadUserSettings();
  }, [user?.id]);

  // Save settings to API
  const saveSettings = useCallback(async (newSettings: SettingsState) => {
    try {
      setIsSaving(true);
      
      console.log('Frontend sending to API:', {
        two_fa_type: newSettings.twoFactorType,
        sms_consent: newSettings.smsConsent,
        fullSettings: newSettings
      });
      
      // Update user settings with all settings
      const response = await apiClient.post('/user/update-settings', {
        user_settings: {
          two_fa_enabled: newSettings.twoFactorAuth,
          sms_consent: newSettings.smsConsent,
          two_fa_type: newSettings.twoFactorType,
        }
      });

      if (response.status !== 200) {
        throw new Error(`Failed to save settings: ${response.statusText}`);
      }

      setHasUnsavedChanges(false);
      toast({
        title: 'Settings saved',
        description: 'Your account settings have been updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Save failed',
        description: 'Failed to save account settings. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [toast]);

  // Debounced save function
  const debouncedSave = useCallback((newSettings: SettingsState) => {
    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Set new timeout for 3 seconds
    const newTimeout = setTimeout(() => {
      saveSettings(newSettings);
    }, 3000);

    setSaveTimeout(newTimeout);
  }, [saveTimeout, saveSettings]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [saveTimeout]);

  const handleSettingChange = (key: keyof SettingsState, value: boolean) => {
    let newSettings = { ...settings, [key]: value };
    
    // If SMS consent is being deselected and 2FA is set to SMS, switch to Email
    if (key === 'smsConsent' && value === false && settings.twoFactorType === 'sms') {
      newSettings = { ...newSettings, twoFactorType: 'email' };
      toast({
        title: '2FA Method Changed',
        description: 'Two-factor authentication has been switched to Email because SMS consent was removed.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
    }
    
    setSettings(newSettings);
    setHasUnsavedChanges(true);
    debouncedSave(newSettings);
  };

  const handleTwoFactorTypeChange = (isSms: boolean) => {
    const newType = isSms ? 'sms' : 'email';
    
    // Check if trying to select SMS but SMS consent is not accepted
    if (newType === 'sms' && !settings.smsConsent) {
      toast({
        title: 'SMS Consent Required',
        description: "We're sorry, you must accept the Text Messaging terms to use Text-based 2FA",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return; // Don't change the setting
    }

    const newSettings = { ...settings, twoFactorType: newType };
    setSettings(newSettings);
    setHasUnsavedChanges(true);
    debouncedSave(newSettings);
  };

  // Show loading state while fetching settings
  if (isLoadingSettings) {
    return (
      <Box mt={4}>
        <Heading size="md" mb={6} color="white">Account Settings</Heading>
        <Text color="gray.300" py={8}>Loading your account settings...</Text>
      </Box>
    );
  }

  return (
    <Box mt={4} style={{textAlign: 'left'}}>
      <Heading size="md" mb={6} color="white">Account Settings</Heading>
      <Text color="gray.300" mb={4}>Manage your account preferences and security settings here.</Text>
      
      
      {/* Two-Factor Authentication Section */}
      <Box mb={8}>
        <Heading size="sm" mb={4} color="white">Security</Heading>
        <Box ml={12}>
          <HStack spacing={4} align="center">
            <Text color="white">Two-factor authentication</Text>
            <Text color="gray.300" fontSize="sm">Off</Text>
            <Switch
              colorScheme="green"
              isChecked={settings.twoFactorAuth}
              onChange={(e) => handleSettingChange('twoFactorAuth', e.target.checked)}
              sx={{
                '& .chakra-switch__track': {
                  bg: '#22a000',
                  _checked: {
                    bg: '#22a000'
                  }
                },
                '& .chakra-switch__thumb': {
                  bg: 'white'
                }
              }}
            />
            <Text color="gray.300" fontSize="sm">On</Text>
          </HStack>
          
          {/* 2FA Method Switch - Only show when 2FA is enabled */}
          {settings.twoFactorAuth && (
            <Box ml={"112px"} mt={3}>
              <HStack spacing={4} align="center">
                <Text color="white">Send Via</Text>
                <Text color="gray.300" fontSize="sm">Email</Text>
                <Switch
                  colorScheme="green"
                  isChecked={settings.twoFactorType === 'sms'}
                  onChange={(e) => handleTwoFactorTypeChange(e.target.checked)}
                  sx={{
                    '& .chakra-switch__track': {
                      bg: '#22a000',
                      _checked: {
                        bg: '#22a000'
                      }
                    },
                    '& .chakra-switch__thumb': {
                      bg: 'white'
                    }
                  }}
                />
                <Text color="gray.300" fontSize="sm">Text</Text>
              </HStack>
            </Box>
          )}
        </Box>
      </Box>

      {/* Text Messaging Section */}
      <Box mb={8}>
        <Heading size="sm" mb={4} color="white">Text Messaging</Heading>
    
        <Box ml={12}>
        <Text color="gray.300" mb={4}>
          Mobile Phone: {user?.phone ? formatPhoneNumber(user.phone) : 'Not provided'}
        </Text>
        <Flex align="flex-start" gap={3}>
          
          <Checkbox
            isChecked={settings.smsConsent}
            onChange={(e) => handleSettingChange('smsConsent', e.target.checked)}
            colorScheme="green"
            mt={1}
            sx={{
              '& .chakra-checkbox__control': {
                borderColor: '#22a000',
                _checked: {
                  bg: '#22a000',
                  borderColor: '#22a000',
                  _hover: {
                    bg: '#1a8000',
                    borderColor: '#1a8000',
                  }
                }
              }
            }}
          />
          <Box fontSize="15px" color="gray.200" lineHeight="1.4">
            <Text>
              I agree to receive <Text as="span" fontWeight="bold">recurring</Text> text messages from <Text as="span" fontWeight="bold">Dominus</Text> (by <Text as="span" fontWeight="bold">Mobile Global Esports</Text>) at the number provided about my account and product updates. <Text as="span" fontWeight="bold">Msg & data rates may apply. Msg frequency varies. Reply STOP to cancel, HELP for help.</Text>
            </Text>
            <Text mt={2}>
              By checking this box, you confirm you're the owner of the number provided and agree to our <Text as="span" color="#22a000" textDecoration="underline" cursor="pointer">SMS Terms</Text> and <Text as="span" color="#22a000" textDecoration="underline" cursor="pointer">Privacy Policy</Text>.
            </Text>
          </Box>
        </Flex>
        </Box>
      </Box>
      
      {/* Save Status Indicator */}
      {hasUnsavedChanges && (
        <Box bg="blue.500" color="white" p={3} borderRadius="md" mb={4} textAlign="center" maxW="200px" mx="auto">
          {isSaving ? 'Saving...' : 'Save my Changes'}
        </Box>
      )}
    </Box>
  );
};

export default SettingsTab;
